import { z } from 'zod'

import { DateZod } from '../common'

/**
 * Authentication token stored in Firestore for pre-authenticated universal links.
 * Tokens are generated by Pilote backend when sending SMS with auth links.
 * They are consumed by the mobile app via Firebase Function for automatic login.
 */
export const AuthTokenZod = z.object({
  /** Phone number in E.164 format (e.g., +15145551234) */
  phoneNumber: z.string(),

  /** Central ID (piloteId) that generated the token */
  centralId: z.number(),

  /** Firestore region document ID for the central */
  regionId: z.string(),

  /** Token creation timestamp (set by Firestore server) */
  createdAt: DateZod,

  /** Token expiration timestamp (24 hours after creation) */
  expiresAt: DateZod,

  /** Timestamp when token was used (null if not yet used) */
  usedAt: DateZod.nullable(),
})

export type AuthToken = z.infer<typeof AuthTokenZod>

/**
 * Type guard to check if a token is expired
 */
export function isAuthTokenExpired(token: AuthToken): boolean {
  return token.expiresAt.getTime() < Date.now()
}

/**
 * Type guard to check if a token has been used
 */
export function isAuthTokenUsed(token: AuthToken): boolean {
  return token.usedAt !== null
}

/**
 * Type guard to check if a token is valid (not expired and not used)
 */
export function isAuthTokenValid(token: AuthToken): boolean {
  return !isAuthTokenExpired(token) && !isAuthTokenUsed(token)
}
